// 2系統コメントログ（§7-4）。可視性は物理分離：
//  - meetingMemo（会議メモ）は経営側にのみ表示・投稿（props.canMeetingMemo）。
//  - staffContact（担当者への連絡）は閲覧権限のある者に表示、投稿可。
import { useEffect, useState } from "react";
import { Button, Spinner, TextArea } from "../../components/ui";
import { formatDate } from "../../lib/format";
import { api, ApiError } from "../../lib/api";
import { t } from "../../i18n/zh-TW";
import type { Comment, CommentChannel } from "../../lib/types";

function CommentList({ comments }: { comments: Comment[] }) {
  if (comments.length === 0) {
    return <p className="text-sm text-slate-400">{t.comments.empty}</p>;
  }
  return (
    <ul className="space-y-2">
      {comments.map((c) => (
        <li key={c.id} className="rounded-lg bg-white px-3 py-2 shadow-sm">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>{c.author_email}</span>
            <span>{formatDate(c.created_at)}</span>
          </div>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{c.body}</p>
        </li>
      ))}
    </ul>
  );
}

function ChannelBoard({
  title,
  hint,
  tone,
  comments,
  onPost,
}: {
  title: string;
  hint: string;
  tone: "memo" | "contact";
  comments: Comment[];
  onPost: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (body.trim() === "") return;
    setBusy(true);
    setError(null);
    try {
      await onPost(body.trim());
      setBody("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t.errors.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  const accent =
    tone === "memo"
      ? "border-slate-300 bg-slate-50"
      : "border-brand-200 bg-brand-50/50";

  return (
    <div className={`rounded-xl border ${accent} p-4`}>
      <h5 className="text-sm font-semibold text-slate-700">{title}</h5>
      <p className="mb-3 text-xs text-slate-500">{hint}</p>
      <CommentList comments={comments} />
      <div className="mt-3">
        <TextArea
          value={body}
          placeholder={t.comments.placeholder}
          onChange={(e) => setBody(e.target.value)}
        />
        {error && <p className="mt-1 text-sm text-red-700">{error}</p>}
        <div className="mt-2 flex justify-end">
          <Button disabled={busy || body.trim() === ""} onClick={submit}>
            {t.comments.postButton}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CommentSection({
  contractId,
  canMeetingMemo,
}: {
  contractId: number;
  canMeetingMemo: boolean;
}) {
  const [staffContact, setStaffContact] = useState<Comment[]>([]);
  const [meetingMemo, setMeetingMemo] = useState<Comment[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    setError(null);
    api
      .getComments(contractId)
      .then((res) => {
        setStaffContact(res.staffContact);
        setMeetingMemo(res.meetingMemo);
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : t.errors.loadFailed))
      .finally(() => setLoading(false));
  }

  useEffect(load, [contractId]);

  // 投稿は全体再読込せず、返ってきた1件を該当チャネルへ楽観追加する。
  // （全体スピナーで一覧・入力欄が消える／失敗時に入力が失われるのを防ぐ）
  async function post(channel: CommentChannel, body: string) {
    const { comment } = await api.postComment(contractId, channel, body);
    if (channel === "meeting_memo") {
      setMeetingMemo((prev) => (prev ? [...prev, comment] : [comment]));
    } else {
      setStaffContact((prev) => [...prev, comment]);
    }
  }

  if (loading) return <Spinner label={t.common.loading} />;
  if (error) return <p className="text-sm text-red-700">{error}</p>;

  return (
    <div className="space-y-4">
      {/* 会議メモは経営側のみ。サーバも meetingMemo:null を返すため二重防御。 */}
      {canMeetingMemo && meetingMemo !== null && (
        <ChannelBoard
          title={t.comments.meetingMemoTitle}
          hint={t.comments.meetingMemoHint}
          tone="memo"
          comments={meetingMemo}
          onPost={(b) => post("meeting_memo", b)}
        />
      )}
      <ChannelBoard
        title={t.comments.staffContactTitle}
        hint={t.comments.staffContactHint}
        tone="contact"
        comments={staffContact}
        onPost={(b) => post("staff_contact", b)}
      />
    </div>
  );
}
